import React, { useState } from 'react';
import { z } from 'zod';
import { ValidatedTextInput } from '../components/ValidatedTextInput';
import Modal, { ModalHeader } from '../utils/Modal';

export const CustomShapeNameModal = ({
  initialValue,
  onClose,
  kind,
}: {
  onClose: (newName: string | null) => void;
  initialValue: string;
  kind: 'create' | 'rename';
}) => {
  const [newShapeName, setNewShapeName] = useState<string | null>(null);

  return (
    <Modal>
      <div>
        <ModalHeader
          onClose={e => {
            // If we don't stop propagation then the click is treated as a click
            // outside of the object menu, which closes it. Thus clicking on the X to
            // cancel the modal would also close the object menu, which we don't want.
            e.stopPropagation();
            onClose(null);
          }}
        >
          {kind === 'create' ? 'Create custom shape' : 'Rename custom shape'}
        </ModalHeader>
        <aha-modal-body>
          <div>
            <form
              onSubmit={e => {
                e.preventDefault();
                if (newShapeName) {
                  onClose(newShapeName);
                }
              }}
            >
              <aha-field autofocus inline-form-field>
                <div slot='label'>Name</div>
                <ValidatedTextInput
                  type='text'
                  data-testid='custom-shape-name-field'
                  initialValue={initialValue}
                  zodValidationSchema={z
                    .string()
                    .min(1, { message: 'Please enter a name' })}
                  onValidation={({ value, error }) => {
                    if (error !== null) {
                      setNewShapeName(null);
                    } else {
                      setNewShapeName(value);
                    }
                  }}
                  autoComplete='off'
                  autoFocus
                />
              </aha-field>
            </form>
          </div>
        </aha-modal-body>
        <aha-modal-footer>
          <aha-button
            slot='secondary_actions'
            onClick={e => {
              // Stop propagation otherwise the click event ends up selecting the last shape
              // to insert from the object menu. Interacting with the modal should not
              // affect anything outside of the modal.
              e.stopPropagation();
              onClose(null);
            }}
          >
            Cancel
          </aha-button>
          <aha-button
            kind='primary'
            disabled={newShapeName === null ? true : undefined}
            onClick={e => {
              // Stop propagation otherwise the click event ends up selecting the last shape
              // to insert from the object menu. Interacting with the modal should not
              // affect anything outside of the modal.
              e.stopPropagation();
              if (!newShapeName) {
                console.error(
                  'Should not be able to submit if new custom shape name is not set'
                );
              } else {
                onClose(newShapeName);
              }
            }}
          >
            {kind === 'create' ? 'Create' : 'Rename'}
          </aha-button>
        </aha-modal-footer>
      </div>
    </Modal>
  );
};

export const CustomShapeConfirmDeleteModal = ({
  onClose,
}: {
  onClose: (result: 'deleteConfirmed' | 'cancelled') => void;
}) => {
  return (
    <Modal
      onClose={() => {
        onClose('cancelled');
      }}
    >
      <div>
        <ModalHeader
          onClose={e => {
            // If we don't stop propagation then the click is treated as a click
            // outside of the object menu, which closes it. Thus clicking on the X to
            // cancel the modal would also close the object menu, which we don't want.
            e.stopPropagation();
            onClose('cancelled');
          }}
        >
          Warning
        </ModalHeader>
        <aha-modal-body-danger>
          <div>Confirm deletion</div>
          <div slot='content'>
            Are you sure you want to delete this custom shape?
          </div>
        </aha-modal-body-danger>
        <aha-modal-footer>
          <aha-button
            slot='secondary_actions'
            onClick={e => {
              e.stopPropagation();
              onClose('cancelled');
            }}
          >
            Cancel
          </aha-button>
          <aha-button
            kind='danger'
            tabIndex={0}
            onClick={e => {
              e.stopPropagation();
              onClose('deleteConfirmed');
            }}
            data-testid='custom-shape-confirm-delete-button'
          >
            Delete
          </aha-button>
        </aha-modal-footer>
      </div>
    </Modal>
  );
};
